import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, RotateCw } from "lucide-react";
import { finErr, finPref } from "@/lib/financeApi";
import { ArchiveViewChips, normView } from "@/components/nds/ArchiveView";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";
import { ListTools, sortRows } from "@/components/nds/ListTools";
import { CardDeck, CardsBack, useCardTabs } from "@/components/nds/TabCards";

export const field = "[color-scheme:dark] bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-sm px-3 py-2 rounded-sm";
export const btn = "px-3 py-1.5 rounded-sm border font-mono text-[10px] tracking-[0.18em] uppercase transition-colors disabled:opacity-40";
export const btnGold = `${btn} border-nex-gold/50 text-nex-gold hover:bg-nex-gold/10`;
export const btnRed = `${btn} border-red-400/40 text-red-300 hover:bg-red-500/10`;
export const btnGreen = `${btn} border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10`;

const TONES = {
  grey: "border-white/15 text-nex-muted", yellow: "border-yellow-400/40 text-yellow-300",
  green: "border-emerald-400/40 text-emerald-300", red: "border-red-400/40 text-red-300", blue: "border-sky-400/40 text-sky-300",
  orange: "border-orange-400/50 text-orange-300", purple: "border-purple-400/50 text-purple-300", darkred: "border-red-800 bg-red-950/60 text-red-400", darkgrey: "border-white/10 bg-white/5 text-white/40",
};
export function Chip({ tone = "grey", children, ...p }) {
  return <span {...p} className={`inline-flex px-2 py-0.5 rounded-full border font-mono text-[9px] tracking-[0.2em] uppercase whitespace-nowrap ${TONES[tone]}`}>{children}</span>;
}

export const Loading = ({ testid, label = "Loading…" }) => (
  <div data-testid={testid} className="flex-1 flex items-center justify-center gap-2 p-10 text-nex-muted text-sm"><Loader2 className="h-4 w-4 animate-spin" /> {label}</div>
);
export const ErrorBox = ({ testid, error, onRetry }) => (
  <div data-testid={testid} className="p-10 text-center space-y-3">
    <p className="text-red-300 text-sm">{error}</p>
    {onRetry && <button type="button" onClick={onRetry} data-testid={`${testid}-retry`} className={btnGold}>Retry</button>}
  </div>
);
export const Empty = ({ testid, children }) => <div data-testid={testid} className="px-5 py-10 text-center text-sm text-nex-muted">{children}</div>;

/** Self-fetching state helper: fetcher returns a promise of data. */
export function useLoad(fetcher, deps) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const seq = useRef(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    const n = ++seq.current;
    setState((s) => ({ ...s, loading: true }));
    return fetcher().then((data) => { if (n === seq.current) setState({ loading: false, data, error: null }); })
      .catch((e) => { if (n === seq.current) setState((s) => ({ ...s, loading: false, error: finErr(e) })); });
  }, deps);
  useEffect(() => { load(); }, [load]);
  return [state, load];
}

/** Resident roster shared by finance cards: search + Active/Archived/All + right-side slot per row. */
export function FinRoster({ prefix, icon: Icon, view, setView, counts, residents, renderRight, onOpen, onRefresh, rowAction, dateOf, owedOf }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("urgent");
  const get = { name: (r) => `${r.first_name} ${r.last_name}`, ...(dateOf ? { date: dateOf } : {}), ...(owedOf ? { owed: owedOf } : {}) };
  const list = sortRows(residents, get, sort, q);
  return (
    <>
      <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-nex-gold" />}
        <ListTools prefix={prefix} q={q} setQ={setQ} sort={sort} setSort={setSort} get={get} className="flex-1" />
        <ArchiveViewChips prefix={prefix} view={view} setView={setView} counts={counts} />
        <button type="button" onClick={onRefresh} data-testid={`${prefix}-refresh`} aria-label="Refresh" className="p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button>
      </div>
      <ul data-testid={`${prefix}-list`} className="flex-1 overflow-y-auto divide-y divide-white/5">
        {list.map((r) => (
          <li key={r.id} className="flex items-center">
            <button type="button" onClick={() => onOpen(r)} data-testid={`${prefix}-row-${r.id}`}
              className="flex-1 min-w-0 text-left px-5 py-3 flex items-center gap-4 hover:bg-nex-gold/5 transition-colors">
              <span className="flex-1 min-w-0 truncate text-nex-silver font-medium">{r.first_name} {r.last_name}</span>
              <OpenResidentButton residentId={r.id} name={`${r.first_name} ${r.last_name}`} testid={`open-resident-${prefix}-${r.id}`} />
              {r.status === "archived" && <Chip tone="orange" data-testid={`${prefix}-archived-${r.id}`}>Archived</Chip>}
              {renderRight(r)}
              <ArrowRight className="h-3.5 w-3.5 text-nex-muted shrink-0" />
            </button>
            {rowAction && <div className="pr-4 shrink-0">{rowAction(r)}</div>}
          </li>
        ))}
        {!list.length && <li><Empty testid={`${prefix}-empty`}>No residents match.</Empty></li>}
      </ul>
    </>
  );
}

export const useView = (key) => {
  const [view, setV] = useState(() => normView(finPref.get(key, "active")));
  return [view, (v) => { finPref.set(key, v); setV(v); }];
};

/** In-place detail shell with a static TABS array: [{ id, label, Tab }]. */
export function DetailShell({ prefix, tabs, resident, title, backLabel = "All residents", onBack, tabProps }) {
  const ct = useCardTabs(tabs[0].id);
  const tab = ct.tab;
  const setTab = ct.open;
  const cur = tabs.find((t) => t.id === tab) || tabs[0];
  const { Tab } = cur;
  return (
    <div data-testid={`${prefix}-detail`} className="flex flex-col h-full">
      <div className="px-5 pt-4 space-y-3 border-b border-white/10">
        <button type="button" onClick={onBack} data-testid={`${prefix}-back`} className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">
          <ArrowLeft className="h-3 w-3" /> {backLabel}
        </button>
        <div className="flex items-center gap-3"><h3 data-testid={`${prefix}-detail-name`} className="text-lg text-nex-silver font-semibold">{title ?? `${resident.first_name} ${resident.last_name}`}</h3>{resident && <OpenResidentButton residentId={resident.id} name={`${resident.first_name} ${resident.last_name}`} testid={`open-resident-${prefix}-detail`} />}</div>
        {ct.compact ? <div className="pb-3" /> : <div className="flex gap-4">
          {tabs.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`${prefix}-tab-${t.id}`} aria-pressed={tab === t.id}
              className={`pb-2 font-mono text-[10px] tracking-[0.2em] uppercase border-b-2 transition-colors ${tab === t.id ? "border-nex-gold text-nex-gold" : "border-transparent text-nex-muted hover:text-nex-silver"}`}>
              {t.label}
            </button>
          ))}
        </div>}
      </div>
      <div ref={ct.ref} className="flex-1 overflow-y-auto">
        {ct.deck ? <div className="p-5"><CardDeck testid={`${prefix}-cards`} items={tabs} onOpen={setTab} /></div>
          : <>{ct.compact && <CardsBack testid={`${prefix}-cards-back`} className="mx-5 mt-4" label={cur.label} onClick={ct.close} />}<Tab resident={resident} {...tabProps} /></>}
      </div>
    </div>
  );
}

export const when = (v) => (v ? new Date(v).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

export const PAYCHECK_CHIP = {
  pending_verification: ["yellow", "Pending"], verified: ["green", "Verified"], flagged: ["red", "Flagged"], voided: ["grey", "Voided"], none: ["grey", "None"],
};
export const REQUEST_CHIP = { pending: ["yellow", "Pending"], approved: ["green", "Approved"], denied: ["red", "Denied"], voided: ["grey", "Voided"] };
export const PENALTY_TYPE_CHIP = { demerit: ["orange", "NCN"], portal_breach: ["red", "Portal breach"], restitution: ["purple", "Restitution"], forfeiture: ["darkred", "Forfeiture"] };
export const PENALTY_STATUS_CHIP = { issued: ["yellow", "Issued"], paid: ["green", "Paid"], waived: ["blue", "Waived"], voided: ["grey", "Voided"] };
export const INVOICE_CHIP = { draft: ["grey", "Draft"], submitted: ["yellow", "Submitted"], approved: ["blue", "Approved"], paid: ["green", "Paid"], voided: ["darkgrey", "Voided"] };
export const VENDOR_TYPE_CHIP = { landlord: ["blue", "Landlord"], labor: ["orange", "Labor"], vendor: ["grey", "Vendor"] };
export const PAYOUT_CHIP = { draft: ["grey", "Draft"], approved: ["blue", "Approved"], paid: ["green", "Paid"], voided: ["darkgrey", "Voided"] };
export const OBLIGATION_CHIP = { current: ["grey", "Current"], partial: ["yellow", "Partial"], paid: ["green", "Paid"], overdue: ["red", "Overdue"], waived: ["darkgrey", "Waived"], voided: ["darkgrey", "Voided"] };

/** Status filter chips persisted under tea_finance_<key>. */
export function StatusFilter({ prefix, chips, value, onChange, counts }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {[["all", "All"], ...Object.entries(chips).map(([k, [, l]]) => [k, l])].map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${prefix}-status-${k}`} aria-pressed={value === k}
          className={`px-3 py-1 rounded-full border font-mono text-[10px] tracking-[0.18em] uppercase transition-colors ${
            value === k ? "border-nex-gold bg-nex-gold/15 text-nex-gold" : "border-white/10 text-nex-muted hover:text-nex-silver hover:border-white/30"}`}>
          {l} · {counts[k] ?? 0}
        </button>
      ))}
    </div>
  );
}

export const SUMMARY_TONE = { green: "border-emerald-400/50 bg-emerald-500/10 text-emerald-300", grey: "border-white/20 bg-white/5 text-nex-muted", blue: "border-sky-400/50 bg-sky-500/10 text-sky-300", red: "border-red-400/50 bg-red-500/10 text-red-300", orange: "border-orange-400/50 bg-orange-500/10 text-orange-300", yellow: "border-yellow-400/50 bg-yellow-500/10 text-yellow-300" };
export const SummaryChip = ({ tone, testid, children }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border font-mono text-[10px] tracking-[0.18em] uppercase ${SUMMARY_TONE[tone]}`}>{children}</span>
);
